const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper function to get admin token
const getAdminToken = () => {
  return localStorage.getItem('adminToken');
};

// Admin JWTs expire after 8h server-side, but the dead token stays in
// localStorage and AdminAuthContext treats mere presence as logged-in. Without
// the handling below, an expired session still renders the whole panel while
// every request 401s, so each section shows an empty table and no error — which
// is indistinguishable from "there is no data", and reloading never clears it.
// AdminAuthContext defers to this layer to force the re-login; this is that.
const ADMIN_SESSION_EXPIRED_MESSAGE =
  'Your admin session has expired. Please log in again.';

const clearAdminSession = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminEmail');
};

// Returns the error to throw so callers read as `throw endAdminSession()` and
// control flow is obvious at the call site. A full page load rather than
// react-router navigation: this module sits outside the router, and reloading
// also drops any admin state still held in memory. The pathname guard keeps a
// 401 raised on the login screen itself from looping.
const endAdminSession = () => {
  clearAdminSession();
  if (window.location.pathname !== '/cpanel/login') {
    window.location.assign('/cpanel/login');
  }
  return new Error(ADMIN_SESSION_EXPIRED_MESSAGE);
};

// Helper function to make API calls
const apiCall = async (endpoint, options = {}) => {
  const token = getAuthToken();
  
  // `...options` must come BEFORE `headers`. Spreading it afterwards let an
  // options object carrying its own `headers` replace the merged object
  // wholesale, silently dropping Content-Type and the Authorization bearer.
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Helper function to make admin API calls
const adminApiCall = async (endpoint, options = {}) => {
  const adminToken = getAdminToken();

  if (!adminToken) {
    throw endAdminSession();
  }

  // Same ordering fix as apiCall: `...options` first, so a caller-supplied
  // headers object cannot wipe out X-Admin-Token.
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Token': adminToken,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    // Checked before parsing: an expired token is the one failure the caller
    // must never render as an empty result.
    if (response.status === 401) {
      throw endAdminSession();
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Admin API request failed');
    }

    return data;
  } catch (error) {
    console.error('Admin API Error:', error);
    throw error;
  }
};

// Application API functions
export const applicationAPI = {
  // Get all applications for current user
  getMyApplications: () => apiCall('/applications/my-applications'),

  // Get current user's application (most recent)
  getCurrentApplication: () => apiCall('/applications/my-application/current'),

  // Get specific application
  getApplication: (id) => apiCall(`/applications/${id}`),
  
  // Get application by ID (for viewing)
  getApplicationById: (id) => apiCall(`/applications/${id}`),

  // Create new application
  createApplication: async (formData) => {
    const token = getAuthToken();

    const data = new FormData();
    data.append('courseType', formData.courseType);
    data.append('fullName', formData.fullName);
    data.append('fathersName', formData.fathersName);
    data.append('category', formData.category);
    data.append('dateOfBirth', formData.dateOfBirth);

    // Everything else the form collects. These were previously gathered from
    // the applicant and then dropped before the request was built, so the
    // server never saw them. The server whitelists these same keys.
    const optionalFields = [
      'aadharNumber', 'sex', 'nationality',
      'correspondenceAddress', 'permanentAddress', 'correspondencePhone',
      'qualifyingExam', 'qualifyingExamStatus', 'qualifyingBoard', 'qualifyingYear',
      'qualifyingSubjects', 'qualifyingMarksObtained', 'qualifyingMaxMarks', 'qualifyingPercentage',
      'highSchoolBoard', 'highSchoolYear', 'highSchoolSubjects',
      'highSchoolMarksObtained', 'highSchoolMaxMarks', 'highSchoolPercentage',
      'placeOfApplication'
    ];
    optionalFields.forEach((field) => {
      if (formData[field] !== undefined && formData[field] !== null && formData[field] !== '') {
        data.append(field, formData[field]);
      }
    });

    // multipart cannot carry nested objects, so the per-subject marks go as JSON.
    if (formData.intermediateSubjects) {
      data.append('intermediateSubjects', JSON.stringify(formData.intermediateSubjects));
    }

    // M.Pharm only: the B.Pharm (Ayurveda) eligibility declaration and the
    // year-wise degree marks. Sent as a 'true'/'false' string and as JSON
    // because multipart carries neither booleans nor nested objects.
    if (formData.courseType === 'mpharm') {
      if (formData.bpharmAyurvedaCompleted) {
        data.append('bpharmAyurvedaCompleted', formData.bpharmAyurvedaCompleted);
      }
      if (formData.bpharmAyurvedaMarks) {
        data.append('bpharmAyurvedaMarks', JSON.stringify(formData.bpharmAyurvedaMarks));
      }
    }

    data.append('photo', formData.photo);
    data.append('signature', formData.signature);

    const response = await fetch(`${API_BASE_URL}/applications`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: data,
    });

    return response.json();
  },


  // NOTE: deleteApplication, submitApplication and updateApplication used to
  // live here. They called DELETE /applications/:id, POST
  // /applications/:id/submit and PUT /applications/:id — none of which exist
  // on the server. Any caller would have received a 404 dressed up as an
  // application error, so they are removed rather than left as traps.

  // Get admit card
  getAdmitCard: (id) => apiCall(`/applications/${id}/admit-card`),

  // Generate admit card
  generateAdmitCard: (id) => apiCall(`/applications/${id}/generate-admit-card`, { method: 'POST' }),
};

// User profile API functions
export const userAPI = {
  // Get user profile with application data
  getProfileWithApplication: () => apiCall('/auth/profile-with-application'),
  
  // Get user profile (legacy)
  getProfile: () => apiCall('/auth/profile'),
  
  // Update user profile
  updateProfile: (userData) => apiCall('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(userData),
  }),
};

// Auth API functions
export const authAPI = {
  login: (credentials) => 
    apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  register: (userData) => 
    apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    }),

  forgotPassword: (email) => 
    apiCall('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token, newPassword) => 
    apiCall('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    }),

  verifyResetToken: (token) => 
    apiCall('/auth/verify-reset-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
};

// Admin API functions
export const adminAPI = {
  // Admin login — verifies credentials server-side and returns a signed admin JWT
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Admin login failed');
    }
    return data;
  },

  // Get all applications
  getApplications: () => adminApiCall('/admin/applications'),

  // Get all applicants (users)
  getApplicants: () => adminApiCall('/admin/applicants'),

  // Update application status
  updateApplicationStatus: (applicationId, status) => 
    adminApiCall(`/admin/applications/${applicationId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  // Reset user password
  resetUserPassword: (userId, newPassword, confirmPassword) => 
    adminApiCall(`/admin/applicants/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword, confirmPassword }),
    }),

  // Get specific application for admin
  getApplication: (applicationId) => adminApiCall(`/admin/applications/${applicationId}`),

  // Get admit card for application (admin)
  getAdmitCard: (applicationId) => adminApiCall(`/admin/admit-card/${applicationId}`),

  // Create application for user (admin)
  createApplicationForUser: (userId, formData) => {
    const adminToken = getAdminToken();
    
    const data = new FormData();
    data.append('userId', userId);
    data.append('courseType', formData.get('courseType'));
    data.append('fullName', formData.get('fullName'));
    data.append('fathersName', formData.get('fathersName'));
    data.append('category', formData.get('category'));
    data.append('dateOfBirth', formData.get('dateOfBirth'));
    if (formData.get('photo')) data.append('photo', formData.get('photo'));
    if (formData.get('signature')) data.append('signature', formData.get('signature'));

    return fetch(`${API_BASE_URL}/admin/applications`, {
      method: 'POST',
      headers: {
        'X-Admin-Token': adminToken,
      },
      body: data,
    }).then(response => {
      if (response.status === 401) {
        throw endAdminSession();
      }
      return response.json();
    });
  },

  // Update application (admin)
  updateApplication: (applicationId, formData) => {
    const adminToken = getAdminToken();
    
    const data = new FormData();
    data.append('courseType', formData.get('courseType'));
    data.append('fullName', formData.get('fullName'));
    data.append('fathersName', formData.get('fathersName'));
    data.append('category', formData.get('category'));
    data.append('dateOfBirth', formData.get('dateOfBirth'));
    if (formData.get('photo')) data.append('photo', formData.get('photo'));
    if (formData.get('signature')) data.append('signature', formData.get('signature'));

    return fetch(`${API_BASE_URL}/admin/applications/${applicationId}`, {
      method: 'PUT',
      headers: {
        'X-Admin-Token': adminToken,
      },
      body: data,
    }).then(response => {
      if (response.status === 401) {
        throw endAdminSession();
      }
      return response.json();
    });
  },

  // Payment-related functions
  getPayments: () => adminApiCall('/payment/admin/payments'),
  
  getPaymentStatistics: () => adminApiCall('/payment/admin/statistics'),

  // Download payments CSV
  downloadPaymentsCsv: async () => {
    const adminToken = getAdminToken();
    const response = await fetch(`${API_BASE_URL}/payment/admin/payments/csv`, {
      method: 'GET',
      headers: {
        'X-Admin-Token': adminToken
      }
    });
    if (response.status === 401) {
      throw endAdminSession();
    }
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to download CSV');
    }
    return response.blob();
  },

  // Analytics
  getAnalytics: () => adminApiCall('/admin/analytics'),

  // Download invigilator sheet PDF
  downloadInvigilatorSheet: () => {
    const adminToken = getAdminToken();
    // Returns the raw Response; callers inspect it themselves. Only the expired
    // session is intercepted here, so it cannot surface as an empty download.
    return fetch(`${API_BASE_URL}/admin/invigilator-sheet-pdf`, {
      method: 'GET',
      headers: {
        'X-Admin-Token': adminToken
      }
    }).then(response => {
      if (response.status === 401) {
        throw endAdminSession();
      }
      return response;
    });
  },
};


const api = { applicationAPI, authAPI, adminAPI, userAPI };

export default api; 